import api from './api';

// Home Depot OTW upload (360 port of v1 cmshomedepot/getotw_csv.pl).
// Endpoints live under /thd-otw-import/*. The axios `api` instance already
// carries the /api/v1 prefix.
const BASE = '/thd-otw-import';

/** A whole-file rejection, AGGREGATED BY CAUSE — one entry per distinct problem,
 *  not per row. One bad mapping errors on every row that uses it, so a single
 *  retired dispatcher can account for hundreds of rows and one line here. */
export interface FileError {
  message: string;
  /** Where the problem is fixed — almost always a maintenance page, because the
   *  common failures here are config rather than bad data in the file. */
  fix_hint: string;
  /** A sample of the source lines that hit this cause (1-based, capped). */
  rows: number[];
  /** Total rows that hit it, including any beyond the sample. */
  count: number;
}

export interface FieldChange {
  column: string;
  old: string;
  new: string;
}

export type Verdict = 'create' | 'update' | 'no_change' | 'old_move' | 'deleted' | 'non_gwi';

/** One collated container and what the run will do with it. Several file rows per
 *  container is normal — `rows` lists the lines it came from. */
export interface ContainerPreview {
  container: string;
  verdict: Verdict;
  reason: string;
  rows: number[];

  dcnumber: string;
  scac: string;
  mode: string;       // what the order will store (CCVT becomes AWR)
  raw_mode: string;   // what the file said
  vessel: string;
  etadate: string;
  dcduedate: string;
  bol: string;
  po: string;
  dept: string;
  size: string;
  equiptype: string;
  kgs: string;
  lbs: string;
  sealnumber: string;

  template: string;
  rail: string;
  dispatcher: string;

  triaxle: string;
  swing: string;
  legdrop: string;

  pronumbers: string[];
  changes: FieldChange[];
  eta_warning: boolean;
  /** Set when legacy CMS's substring BOL matching could have decided this
   *  container differently. Informational — the run does what `verdict` says. */
  legacy_divergence: string;
  /** Columns whose value disagreed between this container's rows. */
  conflicts: string[];
}

export interface PlanCounts {
  create: number;
  update: number;
  no_change: number;
  old_move: number;
  deleted: number;
  non_gwi: number;
}

export interface ValidateResponse {
  ok: boolean;
  filename: string;
  total_rows: number;
  counts: PlanCounts;
  containers: ContainerPreview[];
  errors: FileError[];
  eta_warnings: number;
  legacy_divergences: number;
}

export interface ContainerResult {
  container: string;
  action: string;
  pronumber: string;
  ok: boolean;
  error: string;
  changed: string[];
}

export interface ApplyResponse {
  ok: boolean;
  filename: string;
  batch_id: string;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  results: ContainerResult[];
  pronumbers: string[];
  errors: FileError[];
}

export interface ThdOtwReferenceData {
  columns: string[];
  required_columns: string[];
  modes: string[];
  sizes: string[];
}

/** The `detail` string from a FastAPI HTTPException, or the best available text. */
export function extractDetail(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (e instanceof Error) return e.message;
  return String(e);
}

export async function getReferenceData(): Promise<ThdOtwReferenceData> {
  const res = await api.get(`${BASE}/reference-data`);
  return res.data as ThdOtwReferenceData;
}

function upload(file: File): FormData {
  const form = new FormData();
  form.append('file', file);
  return form;
}

/** Dry run — writes nothing. */
export async function validateFile(file: File): Promise<ValidateResponse> {
  // The explicit multipart header is required; without it the file "goes
  // missing" server-side.
  const res = await api.post(`${BASE}/validate`, upload(file), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as ValidateResponse;
}

/** How many containers one request writes.
 *
 *  A create costs 96 SQL statements, so a run's wall time is per-statement
 *  latency x 96 x containers — ~5.7s per create from a laptop against Azure.
 *  Chunking makes the request timeout irrelevant at ANY latency instead of
 *  correct at one we happened to measure, and gives the user progress on a job
 *  that legitimately takes minutes. */
export const CHUNK_SIZE = 25;

/** Run one chunk. `containers` names the subset; `final` tells the server to
 *  record the System Event for the whole run. */
export async function applyFile(
  file: File,
  opts: { containers?: string[]; batchId?: string; final?: boolean } = {},
): Promise<ApplyResponse> {
  const form = upload(file);
  if (opts.containers) form.append('containers', opts.containers.join(','));
  if (opts.batchId) form.append('batch_id', opts.batchId);
  form.append('final', String(opts.final ?? true));
  const res = await api.post(`${BASE}/apply`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data as ApplyResponse;
}

/** Display label for a verdict, matching the summary strip. */
export const VERDICT_LABELS: Record<Verdict, string> = {
  create: 'Create',
  update: 'Update',
  no_change: 'No change',
  old_move: 'Old move',
  deleted: 'Deleted',
  non_gwi: 'Non-GWI',
};

/** Which verdicts actually write. */
export const WRITES: Verdict[] = ['create', 'update'];
